<template>
    <PrimaryModal
        ref="editNewLoyerModal"
        btnSuccessText="Valider le loyer"
        :disabledBtn="!checkFieldValid"
        @ok="validateLoyer()"
        :width="750"
    >
        <template v-slot:title>
            <div class="flex justify-between items-center">
                <h3 class="text-2xl text-bold">Valider le loyer</h3>
            </div>
        </template>
        <template v-if="loaded" v-slot:content>
            <div class="md:col-span-3">
                <div class="mt-3">
                    <SwitchForm labelRight labelInline v-model='periodePartielle' title="Loyer partiel (entrée ou sortie en cours de mois)" id='switchPeriodePartielle'/>
                </div>
                <div v-if="periodePartielle" class="grid grid-cols-1 md:grid-cols-2">
                    <DatePicker
                        v-model='loyer.periode_debut'
                        class="mt-3 mr-2"
                        title="Début de la période"
                        :validations="['required']"
                        id='periodeDebut'
                    />
                    <DatePicker
                        v-model='loyer.periode_fin'
                        class="mt-3 mr-2"
                        title="Fin de la période"
                        :validations="['required']"
                        id='periodeFin'
                    />
                    <p v-if="prorataPreview" class="md:col-span-2 text-sm text-indigo-500 italic mt-1">
                        Prorata sur {{ prorataPreview.joursOccupes }} jour(s) sur {{ prorataPreview.joursDansLeMois }} :
                        {{ prorataPreview.loyer }} € de loyer + {{ prorataPreview.charges }} € de charges, reporté automatiquement ci-dessous sauf si vous les modifiez vous-même.
                    </p>
                    <p v-else class="md:col-span-2 text-sm text-gray-500 italic mt-1">
                        Sélectionnez les deux dates pour calculer le prorata.
                    </p>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2">
                    <div
                        class="mt-3 mr-2"
                    >
                        <label class="form-label" for="month">
                            Mois*
                        </label>
                        <div
                            class="relative"
                        >
                            <Multiselect
                                v-model='loyer.month'
                                id='month'
                                :options="monthOptions"
                                label="label"
                                track-by="label"
                                v-bind="$multiselectBind"
                            />
                        </div>
                    </div>

                    <DatePicker
                        v-model="loyer.year"
                        class="mt-3 mr-2"
                        title="Année"
                        :validations="['required']"
                        id='year'
                        minimum-type="year"
                        maximum-type="year"
                        @changeYear="(event) => loyer.year = event.toString()"
                    />
                </div>
                <div class="grid grid-cols-1 md:grid-cols-3">
                    <div class="mt-3 mr-2">
                        <AppInput
                            :model-value='loyer.loyer'
                            @update:model-value="onLoyerInput"
                            title='Loyer'
                            id='loyer'
                            :validations="['number']"
                            @hasError="handleFormError"
                            :check-on-create="false"
                            icon="euro-sign"
                        />
                        <p v-if="loyerValeurManuelle" class="text-xs text-orange-500 italic mt-1">
                            Valeur modifiée manuellement, elle diffère du montant calculé par le prorata ({{ prorataPreview.loyer }} €).
                        </p>
                    </div>
                    <div class="mt-3 mr-2">
                        <AppInput
                            :model-value='loyer.charges'
                            @update:model-value="onChargesInput"
                            title='Charges'
                            id='charges'
                            :validations="['number']"
                            @hasError="handleFormError"
                            :check-on-create="false"
                            icon="euro-sign"
                        />
                        <p v-if="chargesValeurManuelle" class="text-xs text-orange-500 italic mt-1">
                            Valeur modifiée manuellement, elle diffère du montant calculé par le prorata ({{ prorataPreview.charges }} €).
                        </p>
                    </div>
                    <DatePicker
                        v-model='loyer.paiement_date'
                        class="mt-3 mr-2"
                        title="Date de paiement"
                        id='paiementDate'
                    />
                </div>
                <div class="grid grid-cols-2">
                    <div
                        class="mt-3 mr-2"
                    >
                        <label class="form-label mb-0" for="month">
                            Habitant(s)*
                        </label>
                        <SelectHabitantList v-model="loyer.habitants" @update="(event) => loyer.habitants = event"/>
                    </div>
                </div>
            </div>
        </template>
        <template v-else v-slot:content>
            <SpinnerLoader class="mt-5"></SpinnerLoader>
        </template>
        <template v-slot:footer>
            <div
                class="mr-2" 
            >
                <SwitchForm labelRight labelInline v-model='sendRentReceip' :title="'Envoyer la '+ (loyer.uuid ? 'nouvelle ' : '') +'quittance de loyer par email'" id='switchSendRentReceip' class="my-3"/>
            </div>
        </template>
    </PrimaryModal>
</template>

<script>

import { inject } from 'vue';
import AppInput from './../../../form/AppInput';
import DatePicker from './../../../form/DatePicker';
import SwitchForm from './../../../form/SwitchForm';
import SelectHabitantList from './../../../form/habitant/SelectHabitantList.vue';
import SpinnerLoader from './../../../loader/SpinnerLoader';
import Multiselect from 'vue-multiselect'
import PrimaryModal from './../../../composant/PrimaryModal';
import { mapGetters } from "vuex";

export default {
    components: {
        PrimaryModal,
        AppInput,
        SwitchForm,
        DatePicker,
        Multiselect,
        SpinnerLoader,
        SelectHabitantList
    },
    data() {
        return {
            loyer: {
                uuid: null,
                year: "",
                month: null,
                loyer: "",
                charges: "",
                datePaiement: null,
                periode_debut: null,
                periode_fin: null,
                habitants: [],
            },
            periodePartielle: 0,
            loyerModifieManuellement: false,
            chargesModifieManuellement: false,
            sendRentReceip: 0,
            formInvalid: [],
            loaded: false
        }
    },

    mounted() {
        const tokenServices = inject('tokenServices')

        this.sendRentReceip = tokenServices.getUserSettingFromKey('default_send_quittance_email') === true ? 1 : 0
    },

    computed: {
        ...mapGetters({
            monthOptions: 'getMonthOptions',
            getMonth: 'getMonth'
        }),
        checkFieldValid() {
            return this.formInvalid.length === 0 &&
            this.loyer.year && this.loyer.month &&
            this.loyer.year.toString().length === 4 && this.loyer.paiement_date &&
            (!this.periodePartielle || (this.loyer.periode_debut && this.loyer.periode_fin && this.loyer.periode_fin >= this.loyer.periode_debut));
        },
        prorataPreview() {
            if (!this.periodePartielle || !this.loyer.periode_debut || !this.loyer.periode_fin || !this.loyer.bien) {
                return null;
            }

            const debut = this.loyer.periode_debut;
            const fin = this.loyer.periode_fin;

            if (fin < debut || debut.getMonth() !== fin.getMonth() || debut.getFullYear() !== fin.getFullYear()) {
                return null;
            }

            const joursDansLeMois = new Date(debut.getFullYear(), debut.getMonth() + 1, 0).getDate();
            const joursOccupes = Math.round((fin - debut) / (1000 * 60 * 60 * 24)) + 1;
            const ratio = joursOccupes / joursDansLeMois;

            return {
                joursOccupes,
                joursDansLeMois,
                loyer: this.loyer.bien.loyer !== null && this.loyer.bien.loyer !== undefined ? Math.round(this.loyer.bien.loyer * ratio * 100) / 100 : null,
                charges: this.loyer.bien.charges !== null && this.loyer.bien.charges !== undefined ? Math.round(this.loyer.bien.charges * ratio * 100) / 100 : null,
            };
        },
        loyerValeurManuelle() {
            return !!(this.loyerModifieManuellement && this.prorataPreview && this.prorataPreview.loyer !== null && parseFloat(this.loyer.loyer) !== this.prorataPreview.loyer);
        },
        chargesValeurManuelle() {
            return !!(this.chargesModifieManuellement && this.prorataPreview && this.prorataPreview.charges !== null && parseFloat(this.loyer.charges) !== this.prorataPreview.charges);
        }
    },

    methods: {
        async openModal(loyer = null) {
            let currentHabitants = []

            if (loyer && loyer.uuid) {
                const {results} = await this.$api.habitant.getHabitants(1, loyer.bien.uuid, {archived: 0});
                currentHabitants = results
            }

            this.$refs.editNewLoyerModal.openModal();

            this.loyer = JSON.parse(JSON.stringify(loyer))
            this.loyer.month = this.getMonth(loyer.month)
            this.loyer.paiement_date = loyer.paiement_date ? new Date(loyer.paiement_date) : new Date()
            this.loyer.periode_debut = loyer.periode_debut ? new Date(loyer.periode_debut) : null
            this.loyer.periode_fin = loyer.periode_fin ? new Date(loyer.periode_fin) : null
            this.periodePartielle = (this.loyer.periode_debut || this.loyer.periode_fin) ? 1 : 0
            // Un loyer déjà prorata conserve ses montants enregistrés : on ne les recalcule pas au chargement,
            // seule une nouvelle saisie de dates (ou une modification manuelle) doit les faire évoluer.
            this.loyerModifieManuellement = !!(this.loyer.periode_debut && this.loyer.periode_fin)
            this.chargesModifieManuellement = !!(this.loyer.periode_debut && this.loyer.periode_fin)

            this.loyer.habitants = loyer.habitants.map(h => {
                return {
                    uuid: h.uuid,
                    selected: 1,
                    data: h
                }
            })

            for (const ch of currentHabitants) {
                const exist = loyer.habitants.filter(h => h.uuid === ch.uuid).length > 0

                if (!exist) {
                    this.loyer.habitants.push({
                        uuid: ch.uuid,
                        selected: 0,
                        data: ch
                    })
                }
            }

            this.loaded = true
        },

        async validateLoyer() {
            if (!this.checkFieldValid || !this.loyer.bien.uuid || !this.loyer.uuid) {
                return;
            }

            this.$refs.editNewLoyerModal.activateLoader();

            await this.$api.bien.updateLoyer(this.loyer.bien.uuid, this.loyer.uuid, {
                year: this.loyer.year.toString(),
                month: this.loyer.month.code,
                loyer: this.loyer.loyer ? parseFloat(this.loyer.loyer) : null,
                charges: this.loyer.charges ? parseFloat(this.loyer.charges) : null,
                paiement_date: this.loyer.paiement_date ? this.loyer.paiement_date.toISOString() : undefined,
                periode_debut: this.periodePartielle ? this.formatDateOnly(this.loyer.periode_debut) : undefined,
                periode_fin: this.periodePartielle ? this.formatDateOnly(this.loyer.periode_fin) : undefined,
                habitants: this.loyer.habitants.filter(h => h.selected).map(h => {
                    return h.uuid
                })
            })
            .then(async (result) => {
                return Promise.resolve(result)
            })
            .catch((err) => {
                console.log(err);
                this.$toast({title: 'Erreur', description: "Une erreur est survenue lors de l'édition, veuillez réessayer plus tard."}, this.$toastError);
            })

            await this.$api.bien.validateLoyer(
                this.loyer.bien.uuid,
                this.loyer.uuid,
                {},
                this.sendRentReceip
            ).then((result) => {
                this.$toast({title: 'Succès', description: "Le loyer à été validé avec succès."}, this.$toastSuccess);

                return Promise.resolve(result)
            })
            .catch((err) => {
                console.log(err);
                this.$toast({title: 'Erreur', description: "Une erreur est survenue lors de l'édition, veuillez réessayer plus tard."}, this.$toastError);
            })    

            this.$refs.editNewLoyerModal.deactivateLoader();
            this.$refs.editNewLoyerModal.dismissModal();
            this.$emit('handleUpdateList');
        },

        onLoyerInput(value) {
            this.loyer.loyer = value;
            this.loyerModifieManuellement = true;
        },

        onChargesInput(value) {
            this.loyer.charges = value;
            this.chargesModifieManuellement = true;
        },

        formatDateOnly(date) {
            if (!date) {
                return undefined;
            }

            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');

            return `${year}-${month}-${day}`;
        },

        handleFormError(event) {
            if (event.value && !this.formInvalid.includes(event.name)) {
                this.formInvalid.push(event.name);
            }

            if (!event.value && this.formInvalid.includes(event.name)) {
                this.formInvalid = this.formInvalid.filter(f => f !== event.name)
            }
        },
    },

    watch: {
        prorataPreview(preview) {
            if (!preview) {
                return;
            }

            if (preview.loyer !== null && !this.loyerModifieManuellement) {
                this.loyer.loyer = preview.loyer;
            }

            if (preview.charges !== null && !this.chargesModifieManuellement) {
                this.loyer.charges = preview.charges;
            }
        }
    }
};
</script>